<template>
  <div class="role-management">
    <!-- 頁面標題 -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>{{ $t('__roleManagement') }}</h2>
      <button class="btn btn-primary" @click="openCreateModal">
        <i class="bi bi-plus-circle me-1"></i>
        {{ $t('__createRole') }}
      </button>
    </div>

    <!-- 搜尋列 -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                :placeholder="$t('__searchRoleName')"
                v-model="searchKeyword"
                @input="handleSearch"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 角色列表 -->
    <div class="card">
      <div class="card-body">
        <b-table
          :items="roles"
          :fields="fields"
          :busy="loading"
          responsive
          hover
          show-empty
          :empty-text="$t('__noData')"
        >
          <!-- 系統角色標記 -->
          <template #cell(name)="data">
            <span>{{ data.value }}</span>
            <span v-if="data.item.is_system_role" class="badge bg-info ms-2">
              {{ $t('__systemRole') }}
            </span>
          </template>

          <!-- 描述 -->
          <template #cell(description)="data">
            <span class="text-muted">{{ data.value || '-' }}</span>
          </template>

          <!-- 權限數量 -->
          <template #cell(permissions_count)="data">
            <span class="badge bg-secondary">{{ data.value }}</span>
          </template>

          <!-- 使用者數量 -->
          <template #cell(users_count)="data">
            <span class="badge bg-primary">{{ data.value || 0 }}</span>
          </template>

          <!-- 建立時間 -->
          <template #cell(created_at)="data">
            <span>{{ formatDate(data.value) }}</span>
          </template>

          <!-- 操作按鈕 -->
          <template #cell(actions)="data">
            <div class="btn-group" role="group">
              <button
                class="btn btn-sm btn-outline-primary"
                @click="openPermissionsModal(data.item)"
                :title="$t('__assignPermissions')"
              >
                <i class="bi bi-shield-check"></i>
              </button>
              <button
                class="btn btn-sm btn-outline-warning"
                @click="openEditModal(data.item)"
                :title="$t('__editRole')"
              >
                <i class="bi bi-pencil"></i>
              </button>
              <button
                v-if="!data.item.is_system_role"
                class="btn btn-sm btn-outline-danger"
                @click="deleteRoleAlert(data.item)"
                :title="$t('__deleteRole')"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </template>

          <!-- Loading 狀態 -->
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle me-2"></b-spinner>
              <strong>{{ $t('__loading') }}...</strong>
            </div>
          </template>
        </b-table>

        <!-- 分頁 -->
        <div v-if="totalRows > perPage" class="d-flex justify-content-center mt-3">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="handlePageChange"
            aria-controls="roles-table"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 新增/編輯角色 Modal -->
    <div
      class="modal fade"
      id="roleFormModal"
      tabindex="-1"
      aria-labelledby="roleFormModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="roleFormModalLabel">
              {{ isEditing ? $t('__editRole') : $t('__createRole') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitRoleForm">
              <!-- 角色名稱 -->
              <div class="mb-3">
                <label for="roleName" class="form-label">
                  {{ $t('__roleName') }} <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg && validate.msg.name }"
                  id="roleName"
                  v-model="roleForm.name"
                  :placeholder="$t('__roleName')"
                />
                <div v-if="validate && validate.msg && validate.msg.name" class="invalid-feedback">
                  {{ validate.msg.name[0] }}
                </div>
              </div>

              <!-- 描述 -->
              <div class="mb-3">
                <label for="roleDescription" class="form-label">
                  {{ $t('__description') }}
                </label>
                <textarea
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg && validate.msg.description }"
                  id="roleDescription"
                  v-model="roleForm.description"
                  rows="3"
                  :placeholder="$t('__description')"
                ></textarea>
                <div v-if="validate && validate.msg && validate.msg.description" class="invalid-feedback">
                  {{ validate.msg.description[0] }}
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('__cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="submitRoleForm" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('__confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 權限分配 Modal -->
    <div
      class="modal fade"
      id="permissionsModal"
      tabindex="-1"
      aria-labelledby="permissionsModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="permissionsModalLabel">
              {{ $t('__assignPermissions') }} - {{ currentRole && currentRole.name }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" style="max-height: 500px; overflow-y: auto;">
            <!-- Loading 狀態 -->
            <div v-if="loadingPermissions" class="text-center my-4">
              <b-spinner class="align-middle me-2"></b-spinner>
              <strong>{{ $t('__loading') }}...</strong>
            </div>

            <!-- 權限列表 -->
            <div v-else>
              <!-- 全選/全不選 -->
              <div class="mb-3">
                <input
                  type="checkbox"
                  class="form-check-input me-2"
                  id="selectAll"
                  v-model="selectAll"
                  @change="toggleSelectAll"
                />
                <label class="form-check-label fw-bold" for="selectAll">
                  {{ $t('__selectAll') }}
                </label>
              </div>

              <!-- 權限卡片網格 -->
              <div class="row">
                <div
                  v-for="(permissions, group) in groupedPermissions"
                  :key="group"
                  class="col-md-6 mb-3"
                >
                  <div class="card">
                    <div class="card-header bg-light">
                      <strong>{{ $t(`__permission_group_${group.replace(/-/g, '_')}`) }}</strong>
                    </div>
                    <div class="card-body">
                      <div
                        v-for="permission in permissions"
                        :key="permission.id"
                        class="form-check mb-2"
                      >
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :id="`permission-${permission.id}`"
                          :value="permission.id"
                          v-model="selectedPermissions"
                        />
                        <label class="form-check-label" :for="`permission-${permission.id}`">
                          {{ $t(`__permission_${permission.name.replace(/\./g, '_').replace(/-/g, '_')}`) }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('__cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="submitPermissions"
              :disabled="submittingPermissions"
            >
              <span v-if="submittingPermissions" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('__confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 刪除確認 Modal -->
    <div
      class="modal fade"
      id="deleteRoleModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="deleteRoleModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__deleteRole") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">{{ $t("__deleteRoleWarning") }}</p>
            <p v-if="deleteTargetRole" class="mt-2 fw-bold">{{ deleteTargetRole.name }}</p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="deleteRole"
              type="button"
              class="btn btn-danger rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import { role } from '@/http/api/role.js';
import { mapState, mapMutations } from 'vuex';
import moment from 'moment';
import i18n from '@/lang/lang.js';

export default {
  name: 'RoleManagement',
  computed: {
    ...mapState(['token'])
  },
  data() {
    return {
      // 列表資料
      roles: [],
      loading: false,
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      searchKeyword: '',
      searchTimer: null,

      // 表格欄位
      fields: [
        { key: 'name', label: this.$t('__roleName'), sortable: true },
        { key: 'description', label: this.$t('__description') },
        { key: 'permissions_count', label: this.$t('__permissionsCount'), class: 'text-center' },
        { key: 'users_count', label: this.$t('__usersCount'), class: 'text-center' },
        { key: 'created_at', label: this.$t('__createdAt'), sortable: true },
        { key: 'actions', label: this.$t('__action'), class: 'text-center' }
      ],

      // 表單相關
      isEditing: false,
      roleForm: {
        id: null,
        name: '',
        description: ''
      },
      validate: null,
      submitting: false,

      // 權限相關
      currentRole: null,
      groupedPermissions: {},
      selectedPermissions: [],
      loadingPermissions: false,
      submittingPermissions: false,
      selectAll: false,

      // Modal 實例
      roleFormModal: null,
      permissionsModal: null,
      deleteRoleModal: null,
      deleteTargetRole: null
    };
  },

  mounted() {
    // 初始化 Bootstrap Modal
    this.roleFormModal = new Modal(document.getElementById('roleFormModal'));
    this.permissionsModal = new Modal(document.getElementById('permissionsModal'));
    this.deleteRoleModal = new Modal(document.getElementById('deleteRoleModal'));

    // 載入角色列表
    this.loadRoles();
  },

  methods: {
    ...mapMutations(['AlertsInfo', 'Alerted']),
    // 載入角色列表
    async loadRoles() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        };

        if (this.searchKeyword) {
          params.search = this.searchKeyword;
        }

        const response = await role.get(params, this.token);

        if (response.data && response.data.status === 'success') {
          this.roles = response.data.data.data;
          this.totalRows = response.data.data.total;
          this.currentPage = response.data.data.current_page;
        }
      } catch (error) {
        console.error('載入角色列表失敗:', error);
        this.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response && error.response.data && error.response.data.message || i18n.t('__loadFailed')
        });
        this.Alerted();
      } finally {
        this.loading = false;
      }
    },

    // 搜尋處理（防抖）
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadRoles();
      }, 500);
    },

    // 分頁處理
    handlePageChange(page) {
      this.currentPage = page;
      this.loadRoles();
    },

    // 開啟新增 Modal
    openCreateModal() {
      this.isEditing = false;
      this.roleForm = {
        id: null,
        name: '',
        description: ''
      };
      this.validate = null;
      this.roleFormModal.show();
    },

    // 開啟編輯 Modal
    async openEditModal(roleItem) {
      this.isEditing = true;
      this.validate = null;

      try {
        const response = await role.get(roleItem.id, this.token);
        if (response.data && response.data.status === 'success') {
          this.roleForm = {
            id: response.data.data.id,
            name: response.data.data.name,
            description: response.data.data.description || ''
          };
          this.roleFormModal.show();
        }
      } catch (error) {
        console.error('載入角色詳情失敗:', error);
        this.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response && error.response.data && error.response.data.message || i18n.t('__loadFailed')
        });
        this.Alerted();
      }
    },

    // 提交表單
    async submitRoleForm() {
      this.validate = null;
      this.submitting = true;

      try {
        let response;
        if (this.isEditing) {
          response = await role.update(this.roleForm.id, {
            name: this.roleForm.name,
            description: this.roleForm.description
          }, this.token);
        } else {
          response = await role.create({
            name: this.roleForm.name,
            description: this.roleForm.description
          }, this.token);
        }

        if (response.data && response.data.status === 'success') {
          this.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: i18n.t('__operationSuccess')
          });
          this.Alerted();
          this.roleFormModal.hide();
          this.loadRoles();
        }
      } catch (error) {
        console.error('提交角色失敗:', error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.validate = { msg: error.response.data.errors };
        } else {
          this.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response && error.response.data && error.response.data.message || i18n.t('__saveFailed')
          });
          this.Alerted();
        }
      } finally {
        this.submitting = false;
      }
    },

    // 顯示刪除確認 Modal
    deleteRoleAlert(roleItem) {
      this.deleteTargetRole = roleItem;
      this.deleteRoleModal.show();
    },

    // 執行刪除
    async deleteRole() {
      const vm = this;
      try {
        const response = await role.delete(vm.deleteTargetRole.id, vm.token);
        if (response.data && response.data.status === 'success') {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: i18n.t('__deleteRole') + i18n.t('__success')
          });
          vm.Alerted();
          vm.loadRoles();
          vm.deleteRoleModal.hide();
          vm.deleteTargetRole = null;
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: response.data.errMsg || i18n.t('__deleteFailed')
          });
          vm.Alerted();
        }
      } catch (error) {
        console.error('刪除角色失敗:', error);
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response && error.response.data && error.response.data.message || i18n.t('__deleteFailed')
        });
        vm.Alerted();
      }
    },

    // 開啟權限分配 Modal
    async openPermissionsModal(roleItem) {
      this.currentRole = roleItem;
      this.loadingPermissions = true;
      this.selectedPermissions = [];
      this.selectAll = false;
      this.permissionsModal.show();

      try {
        // 同時載入可用權限和角色當前權限
        const [availableResponse, roleResponse] = await Promise.all([
          role.getAvailablePermissions(this.token),
          role.get(roleItem.id, this.token)
        ]);

        if (availableResponse.data && availableResponse.data.status === 'success') {
          this.groupedPermissions = availableResponse.data.data;
        }

        if (roleResponse.data && roleResponse.data.status === 'success') {
          this.selectedPermissions = roleResponse.data.data.permissions.map(p => p.id);
          this.updateSelectAllState();
        }
      } catch (error) {
        console.error('載入權限失敗:', error);
        this.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response && error.response.data && error.response.data.message || i18n.t('__loadFailed')
        });
        this.Alerted();
      } finally {
        this.loadingPermissions = false;
      }
    },

    // 全選/全不選
    toggleSelectAll() {
      if (this.selectAll) {
        // 全選：收集所有權限 ID
        this.selectedPermissions = [];
        Object.values(this.groupedPermissions).forEach(permissions => {
          permissions.forEach(permission => {
            this.selectedPermissions.push(permission.id);
          });
        });
      } else {
        // 全不選
        this.selectedPermissions = [];
      }
    },

    // 更新全選狀態
    updateSelectAllState() {
      const allPermissionIds = [];
      Object.values(this.groupedPermissions).forEach(permissions => {
        permissions.forEach(permission => {
          allPermissionIds.push(permission.id);
        });
      });
      this.selectAll = allPermissionIds.length > 0 &&
                       allPermissionIds.every(id => this.selectedPermissions.includes(id));
    },

    // 提交權限變更
    async submitPermissions() {
      this.submittingPermissions = true;

      try {
        const response = await role.assignPermissions(this.currentRole.id, {
          permission_ids: this.selectedPermissions
        }, this.token);

        if (response.data && response.data.status === 'success') {
          this.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: i18n.t('__permissionsUpdated')
          });
          this.Alerted();
          this.permissionsModal.hide();
          this.loadRoles();
        }
      } catch (error) {
        console.error('更新權限失敗:', error);
        this.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response && error.response.data && error.response.data.message || i18n.t('__saveFailed')
        });
        this.Alerted();
      } finally {
        this.submittingPermissions = false;
      }
    },

    // 格式化日期時間
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('YYYY-MM-DD HH:mm');
    }
  },

  watch: {
    // 監聽選中權限變化，更新全選狀態
    selectedPermissions: {
      handler() {
        this.updateSelectAllState();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.role-management {
  padding: 20px;
}

.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.badge {
  font-size: 0.875rem;
}

.btn-group .btn {
  margin: 0 2px;
}

.form-check {
  padding-left: 1.5rem;
}

.card-header {
  font-weight: 600;
  padding: 0.75rem 1rem;
}

.modal-body .card {
  border: 1px solid #dee2e6;
}

.modal-body .card-body {
  padding: 1rem;
  max-height: 300px;
  overflow-y: auto;
}

/* 提升使用體驗的小細節 */
.btn-group .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.input-group-text {
  background-color: #f8f9fa;
}
</style>
